import {
  createMemoryHistory,
  createRootRoute,
  createRoute,
  createRouter,
} from "@tanstack/react-router";

import { BlockPlanTrialDowngrade } from "./BlockPlanTrialDowngrade";
import { UPGRADE_PATHS } from "./constants";
import { EmailHubSubscriptionCheck } from "./EmailHubSubscriptionCheck";
import { CancelScheduledDowngradeRoute } from "./routes/CancelScheduledDowngradeRoute";
import { CanDowngradeRoute } from "./routes/CanDowngradeRoute";
import { ErrorRoute } from "./routes/ErrorRoute";
import { LoadingRoute } from "./routes/LoadingRoute";
import { PlanDetailsRoute } from "./routes/PlanDetailsRoute";
import { ProrationRoute } from "./routes/ProrationRoute";
import { RenewalRoute } from "./routes/RenewalRoute";
import { ScheduledDowngradeConfirmationRoute } from "./routes/ScheduledDowngradeConfirmationRoute";
import { ScheduledDowngradeSuccessRoute } from "./routes/ScheduledDowngradeSuccessRoute";
import { UpgradeSuccessfulRoute } from "./routes/UpgradeSuccessfulRoute";
import { WorkflowAppSyncingRoute } from "./routes/WorkflowAppSyncingRoute";
import { UpgradePlanRootLayout } from "./UpgradePlanRootLayout";

const rootRoute = createRootRoute({
  component: UpgradePlanRootLayout,
});

const cancelScheduledDowngradeRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: UPGRADE_PATHS.CANCEL_SCHEDULED_DOWNGRADE_MODAL,
  component: CancelScheduledDowngradeRoute,
});

const blockPlanTrialDowngradeRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: UPGRADE_PATHS.BLOCK_PLAN_TRIAL_DOWNGRADE,
  component: BlockPlanTrialDowngrade,
});

const planDetailsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: UPGRADE_PATHS.PLAN_DETAILS,
  component: PlanDetailsRoute,
});

const scheduledDowngradeConfirmationRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: UPGRADE_PATHS.SCHEDULED_DOWNGRADE_CONFIRMATION,
  component: ScheduledDowngradeConfirmationRoute,
});

const emailHubSubscriptionCheckRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: UPGRADE_PATHS.EMAIL_HUB_SUBSCRIPTION_CHECK,
  component: EmailHubSubscriptionCheck,
});

const prorationRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: UPGRADE_PATHS.PRORATION,
  component: ProrationRoute,
});

const canDowngradeRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: UPGRADE_PATHS.CAN_DOWNGRADE,
  component: CanDowngradeRoute,
});

const loadingRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: UPGRADE_PATHS.LOADING,
  component: LoadingRoute,
});

const workflowAppSyncingRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: UPGRADE_PATHS.WORKFLOW_APP_SYNCING,
  component: WorkflowAppSyncingRoute,
});

const errorRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: UPGRADE_PATHS.ERROR,
  component: ErrorRoute,
});

const upgradeSuccessfulRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: UPGRADE_PATHS.UPGRADE_SUCCESSFUL,
  component: UpgradeSuccessfulRoute,
});

const scheduledDowngradeSuccessRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: UPGRADE_PATHS.SCHEDULED_DOWNGRADE_SUCCESS,
  component: ScheduledDowngradeSuccessRoute,
});

const renewalRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: UPGRADE_PATHS.RENEWAL,
  component: RenewalRoute,
});

const routeTree = rootRoute.addChildren([
  cancelScheduledDowngradeRoute,
  blockPlanTrialDowngradeRoute,
  planDetailsRoute,
  scheduledDowngradeConfirmationRoute,
  emailHubSubscriptionCheckRoute,
  prorationRoute,
  canDowngradeRoute,
  loadingRoute,
  workflowAppSyncingRoute,
  errorRoute,
  upgradeSuccessfulRoute,
  scheduledDowngradeSuccessRoute,
  renewalRoute,
]);

export type UpgradePlanRouter = ReturnType<typeof createUpgradePlanRouter>;

export const createUpgradePlanRouter = (initialPath: string) => {
  const history = createMemoryHistory({
    initialEntries: [initialPath],
    initialIndex: 0,
  });

  return createRouter({
    routeTree,
    history,
  });
};
